import isNil from 'lodash/isNil';
import { observable, action, makeObservable } from 'mobx';
import { APIClient as ScaffoldClient, FetchUserResponse, SignOutUserResponse } from '@groma-dev/scaffold';

import {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  PasswordResetMFARequest,
  PasswordResetMFAResponse,
  ResetPasswordConfirmRequest,
  ResetPasswordConfirmResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SignInRequest,
  SignInResponse,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  UpdateUserDetailsRequest,
  UpdateUserDetailsResponse,
} from '../apiclient/APIClientInterface';
import { APIClient } from '../apiclient/APIClient';
import { AppConfig } from '../AppConfig';
import { User } from '../models';
import {
  AcceptInvitationStateInterface,
  ResetPasswordStateInterface,
  ScaffoldClientHeaders,
  SignInStateInterface,
  UserStateInterface,
} from './DataStoreInterface';

const signInInitialState: SignInStateInterface = {
  isSubmitting: false,
};

const userInitialState: UserStateInterface = {
  fetchingUser: false,
  user: undefined,
};

const resetPasswordInitialState: ResetPasswordStateInterface = {
  isSubmitting: false,
};

const acceptInvitationInitialState: AcceptInvitationStateInterface = {
  isSubmitting: false,
};

export default class DataStore {
  /**
   * CLASS VARIABLES
   */
  private static instance: any = null;

  /**
   * MOBX OBSERVABLES
   */
  @observable
  signInState = signInInitialState;

  @observable
  userState = userInitialState;

  @observable
  resetPasswordState = resetPasswordInitialState;

  @observable
  acceptInvitationState = acceptInvitationInitialState;

  constructor() {
    if (isNil(DataStore.instance)) {
      DataStore.instance = this;
    }

    makeObservable(this);

    // DataStore is a singleton class
    // eslint-disable-next-line no-constructor-return
    return DataStore.instance;
  }

  /**
   * MOBX ACTIONS
   */
  @action
  signInUser = async (request: SignInRequest): Promise<SignInResponse> => {
    const response = await APIClient.signInRequest(request);

    return response;
  };

  @action
  fetchUser = async (setUser = true): Promise<FetchUserResponse> => {
    this.setFetchingUser(true);
    const { baseUrl } = AppConfig.Settings.Server;
    const response: FetchUserResponse = await ScaffoldClient.fetchUser({
      baseURL: baseUrl,
      headers: ScaffoldClientHeaders,
    });

    if (response.success && setUser) this.setUser(response.user);

    this.setFetchingUser(false);

    return response;
  };

  @action
  signOutUser = async (): Promise<SignOutUserResponse> => {
    const { baseUrl } = AppConfig.Settings.Server;
    const response = await ScaffoldClient.signOutUser({
      baseURL: baseUrl,
      headers: ScaffoldClientHeaders,
    });

    if (response.success) this.setUser();

    return response;
  };

  @action
  updatePassword = async (request: UpdatePasswordRequest): Promise<UpdatePasswordResponse> => {
    const response = await APIClient.updatePassword(request);

    return response;
  };

  @action
  resetPassword = async (request: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    const response = await APIClient.resetPassword(request);

    return response;
  };

  @action
  resetPasswordConfirm = async (request: ResetPasswordConfirmRequest): Promise<ResetPasswordConfirmResponse> => {
    const response = await APIClient.resetPasswordConfirm(request);

    return response;
  };

  @action
  passwordResetMFA = async (request: PasswordResetMFARequest): Promise<PasswordResetMFAResponse> => {
    const response = await APIClient.passwordResetMFA(request);

    return response;
  };

  @action
  acceptInvitation = async (request: AcceptInvitationRequest): Promise<AcceptInvitationResponse> => {
    const response = await APIClient.acceptInvitation(request);

    return response;
  };

  @action
  updateUserDetails = async (request: UpdateUserDetailsRequest): Promise<UpdateUserDetailsResponse> => {
    const response = await APIClient.updateUserDetails(request);

    if (response.success) this.setUser(response.user);

    return response;
  };

  @action
  setIsSigningIn = (value: boolean) => {
    this.signInState.isSubmitting = value;
  };

  @action
  setFetchingUser = (value: boolean) => {
    this.userState.fetchingUser = value;
  };

  @action
  setUser = (user?: User) => {
    this.userState.user = user;
  };

  @action
  setIsResettingPassword = (value: boolean) => {
    this.resetPasswordState.isSubmitting = value;
  };

  @action
  setIsAcceptingInvitation = (value: boolean) => {
    this.acceptInvitationState.isSubmitting = value;
  };
}
